import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import "./ThankYou.css";

import avgImg from "../game/img/ch6.png";
import goodImg from "../game/img/ch4.png";
import bestImg from "../game/img/ch3.png";
import awesomeImg from "../game/img/ch2.png";
import feedbackBanner from "../../img/feedback.png";

const FEEDBACK_STORAGE_KEY = "bug_smasher_feedback_v1";

const ThankYou = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { data: themeData } = useSelector((state) => state.theme);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(setBackButtonUrl("/leaderboard"));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  const token = useMemo(() => {
    const sessionId = user?.sessionId;
    const organizationId = user?.organizationId;
    if (!sessionId || !organizationId) return "";
    return `${sessionId}&${organizationId}`;
  }, [user?.organizationId, user?.sessionId]);

  const feedbackStorageKey = useMemo(() => {
    const sessionId = user?.sessionId || "";
    const organizationId = user?.organizationId || "";
    const userId = user?.userId || user?.id || "";
    // Scope to current player so a previous user's choice doesn't lock a new user.
    return `${FEEDBACK_STORAGE_KEY}::${organizationId}::${sessionId}::${userId}`;
  }, [user?.id, user?.organizationId, user?.sessionId, user?.userId]);

  const feedbackOptions = useMemo(
    () => [
      { id: "AVERAGE", label: "AVERAGE", sub: "Keep Improving!", stars: 1, img: avgImg, tone: "avg" },
      { id: "GOOD", label: "GOOD", sub: "You're Doing Good!", stars: 2, img: goodImg, tone: "good" },
      { id: "BEST", label: "BEST", sub: "Great Job!", stars: 3, img: bestImg, tone: "best" },
      { id: "AWESOME", label: "AWESOME", sub: "You're Amazing!", stars: 4, img: awesomeImg, tone: "awesome" },
    ],
    [],
  );

  useEffect(() => {
    try {
      // Reset selection when player changes, then restore only for this player/session.
      const existing = sessionStorage.getItem(feedbackStorageKey) || "";
      setSelected(existing || "");
    } catch {
      /* ignore */
    }
  }, [feedbackStorageKey]);

  const saveFeedback = async (value) => {
    if (!value || saving) return;
    if (selected) return; // lock once chosen
    setSaving(true);
    try {
      const sessionId = user?.sessionId;
      const organizationId = user?.organizationId;
      const userId = user?.userId || user?.id;
      if (!token || !sessionId || !organizationId || !userId) {
        throw new Error("Missing session to save feedback");
      }

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/submitFeedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          sessionId,
          organizationId,
          feedback: value,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || "Failed to save feedback");
      }
      setSelected(value);
      try {
        sessionStorage.setItem(feedbackStorageKey, value);
      } catch {
        /* ignore */
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="thank-you-page">
      <div className="feedback-hero-banner-wrap">
        <img
          src={feedbackBanner}
          alt="We value your feedback"
          className="feedback-hero-banner"
          draggable={false}
        />
      </div>

      <div className="feedback-card">
        <div className="feedback-hero">
          <div
            className="feedback-subtitle"
            style={{ color: themeData?.text_color || undefined }}
          >
            {themeData?.feedback_title || "How would you rate your experience with Bug Smasher?"}
          </div>
          <div className="feedback-hint">
            <span className="feedback-hint-arrow">↳</span> Tap on a bug to rate{" "}
            <span className="feedback-hint-arrow">↰</span>
          </div>
        </div>

        <div className={`feedback-grid ${selected ? "is-locked" : ""}`}>
          {feedbackOptions.map((opt) => {
            const isSelected = selected === opt.id;
            const isDisabled = Boolean(selected) && !isSelected;
            return (
              <button
                key={opt.id}
                type="button"
                className={`feedback-tile tone-${opt.tone} ${isSelected ? "is-selected" : ""}`}
                disabled={isDisabled || saving}
                onClick={() => saveFeedback(opt.id)}
              >
                <img className="feedback-bug" src={opt.img} alt="" aria-hidden draggable={false} />
                <div className="feedback-label">{opt.label}</div>
                <div className="feedback-sub">{opt.sub}</div>
              </button>
            );
          })}
        </div>

        

        <div className="feedback-small-note">
          <span role="img" aria-hidden="true">
            ❤️
          </span>{" "}
          Your feedback helps us make Bug Smasher even better!
        </div>

        <div className="feedback-footer">
          <button type="button" className="thank-you-primary" onClick={() => navigate("/leaderboard")}>
            Leaderboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
